/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import sendPosthogEvent from '@web/utils/send-posthog-event'
import PlaceholderWrapper from './placeholder-wrapper'
import type {
	MktoOnSuccessCallbackArgs,
	MktoOnErrorCallbackArgs,
	MktoOnStartCallbackArgs,
} from './types'
import { prepareForm } from './prepare-form'

/**
 * More on the methods described in this type at the [Marketo Forms API Reference](https://experienceleague.adobe.com/en/docs/marketo-developer/marketo/javascriptapi/forms-api-reference#mktoforms2-methods).
 */
export interface MarketoFormProps {
	/**
	 * A Marketo form ID.
	 */
	formId?: string

	/**
	 * Sets the size of the form's placeholder.
	 */
	placeholderSize?: 'sm' | 'lg'

	/**
	 * A callback that runs when the form is finished loading. Passed directly to the form preparation function.
	 */
	loadCallback?: () => void

	/**
	 * A class name added to the form's root element. Passed directly to the form preparation function.
	 */
	className?: string

	/**
	 * Replaces the text of the submit button. Passed directly to the form preparation function.
	 */
	submitTitle?: string

	/**
	 * An object with initial values for the form.
	 * If set, the Marketo method `form.setValues()`
	 * is called with `initialValues` as its argument.
	 *
	 * Passed directly to the form preparation function.
	 */
	initialValues?: Record<string, any>

	/**
	 * This callback executes when the form is successfully submitted.
	 * Passed directly to the form preparation function.
	 */
	onSubmitSuccess?: MktoOnSuccessCallbackArgs

	/**
	 * This callback executes when the form has errors during submission.
	 * Passed directly to the form preparation function.
	 */
	onSubmitError?: MktoOnErrorCallbackArgs

	/**
	 * This callback executes on form submission, regardless of submission success.
	 * Passed directly to the form preparation function.
	 */
	onSubmitStart?: MktoOnStartCallbackArgs

	/**
	 * If set, the Marketo method `form.showErrorMessage()`
	 * is called with `errorText` as its argument.
	 */
	errorText?: string

	/**
	 * Defaults to true. If false, badges such as `Required`
	 * won't appear. Passed directly to the form preparation function.
	 */
	allowBadges?: boolean

	/**
	 * If false, the form will not persist field values in local storage.
	 * Passed directly to the form preparation function.
	 * Default: true
	 */
	persistFieldValues?: boolean

	/**
	 * An alternative `aria-label` value for the form element root.
	 * Passed directly to the form preparation function.
	 */
	formName?: string
}

type FormLoadingState = 'loading' | 'ready' | 'error'

export const MarketoForm = ({ formId, ...rest }: MarketoFormProps) => {
	const t = useTranslations()
	const initialized = useRef(false)
	const [formLoadingState, setFormLoadingState] =
		useState<FormLoadingState>('loading')

	useEffect(() => {
		if (!initialized.current && typeof window !== undefined) {
			initialized.current = true
			window.MktoForms2?.loadForm(
				`//${process.env.MARKETO_BASE_URL}`,
				process.env.MARKETO_MUNCHKIN_ID,
				formId,
				(form) => {
					if (!form) {
						setFormLoadingState('error')
						sendPosthogEvent('Form not found', { form_id: formId })
					} else {
						prepareForm(form, rest, t('Required'), () => {
							setFormLoadingState('ready')
						})
					}
				},
			)
		}
	}, [formId])

	return (
		<>
			{formLoadingState === 'loading' ? (
				<PlaceholderWrapper size={rest.placeholderSize} />
			) : (
				<></>
			)}
			{formLoadingState === 'error' ? (
				<p>
					Unfortunately, something went wrong. Please try this form again later.
				</p>
			) : null}
			<form id={`mktoForm_${formId}`} />
		</>
	)
}

MarketoForm.displayName = 'MarketoForm'
